import {Injectable} from '@angular/core';
import {AuthGuard} from '../common/auth-guard.service';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {Machine} from "../model/machine";
import {MachinePersistRequest} from "../model/requests/machine-persist-request";
import {GisMachine} from "../model/gis-machine";
import {MachineFilters} from "../model/filters/machine-filters";
import {HttpUtils} from "../shared/http.utils";
import {MachineLastValues} from '../model/custom/machine/machine-last-values';
import {MachineLastValuesCharts} from '../model/custom/machine/machine-last-values-charts';
import {MachineChartsInfoResponse} from '../model/responses/machine-charts-info-response';
import {MachineDataInfo} from '../model/responses/machine-data-info-response';
import {CommandSchemasResponse} from "../model/responses/command-schemas-response";
import {Measure} from "../model/measure";
import {MachineGeneralInfo} from "../model/custom/machine/machine-general-info";
import {FiltersUtils} from "../shared/filters.utils";
import {CommandPersistRequest} from "../model/requests/command-persist-request";

@Injectable({
  providedIn: 'root'
})
export class MachinesService {

  private readonly machinesURL: string;


  constructor(private authGuard: AuthGuard, private http: HttpClient) {
    this.machinesURL = '/api/machines';
  }

  public getMachines(offset?: number, limit?: number, filters?: MachineFilters): Observable<any> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    if (filters != null) {
      options = MachinesService.setParameters(options, filters);
    }
    return this.http.get<any>(this.machinesURL, options);
  }

  public getMachinesSummary(): Observable<{ machines: Machine[] }> {
    return this.http.get<any>(this.machinesURL + "/summary");
  }

  public getMachinesMinSummary(): Observable<{ machines: Machine[] }> {
    return this.http.get<any>(this.machinesURL + "/short");
  }

  public getMachine(machineUuid: string): Observable<{ machine: Machine }> {
    return this.http.get<{ machine: Machine }>(`${this.machinesURL}/${machineUuid}`);
  }

  public getMachineMap(machineUuid: string): Observable<{ machine: Machine }> {
    return this.http.get<{ machine: Machine }>(`${this.machinesURL}/map/${machineUuid}`);
  }

  public getMachineGeneralInfo(machineUuid: string, viewInLiters: boolean): Observable<{
    machineGeneralInfo: MachineGeneralInfo
  }> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    options.params["viewInLiters"] = viewInLiters ?? '';
    return this.http.get<{
      machineGeneralInfo: MachineGeneralInfo
    }>(`${this.machinesURL}/general/${machineUuid}`, options);
  }

  public getMachineLastValuesByUuid(machineUuid: string, viewInLiters: boolean): Observable<{
    machineLastValues: MachineLastValues
  }> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    options.params["viewInLiters"] = viewInLiters ?? '';
    return this.http.get<{
      machineLastValues: MachineLastValues
    }>(`${this.machinesURL}/data/last-values/${machineUuid}`, options);
  }

  public getMachineLastValuesChartsByUuid(machineUuid: string, viewInLiters: boolean): Observable<{
    machineLastValuesCharts: MachineLastValuesCharts
  }> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    options.params["viewInLiters"] = viewInLiters ?? '';
    return this.http.get<{
      machineLastValuesCharts: MachineLastValuesCharts
    }>(`${this.machinesURL}/charts/last-values/${machineUuid}`, options);
  }

  public getMachinePeriodDataInfoByUuid(machineUuid: string, from: number, until: number, viewInLiters: boolean): Observable<MachineDataInfo> {
    let options = MachinesService.setPeriodParameters(from, until, viewInLiters);
    return this.http.get<MachineDataInfo>(`${this.machinesURL}/data/info/${machineUuid}`, options);
  }

  public getMachineMeasurements(machineUuid: string, from: number, until: number, measurementsUuids: string[]):
    Observable<{ measures: { [key: string]: Measure[] } }> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    options.params["from"] = from || '';
    options.params["until"] = until || '';
    options.params["measurementsUuids"] = measurementsUuids || '';
    return this.http.get<{
      measures: { [key: string]: Measure[] }
    }>(`${this.machinesURL}/data/measurements/${machineUuid}`, options);
  }

  public getAllMachineMeasurements(machineUuid: string): Observable<{ measurements: Map<string, string> }> {
    return this.http.get<{
      measurements: Map<string, string>
    }>(`${this.machinesURL}/data/all-measurements/${machineUuid}`);
  }

  public getMachinePeriodChartInfoByUuid(machineUuid: string, from: number, until: number, viewInLiters: boolean): Observable<MachineChartsInfoResponse> {
    let options = MachinesService.setPeriodParameters(from, until, viewInLiters);
    return this.http.get<MachineChartsInfoResponse>(`${this.machinesURL}/charts/info/${machineUuid}`, options);
  }

  public getAlarmDeviceMeasurements(): Observable<{ deviceMeasurements: string[] }> {
    const url: string = `${this.machinesURL}/measurements/alarms`;
    return this.http.get<{ deviceMeasurements: string[] }>(url);
  }

  public getGisMachines(filters?: MachineFilters): Observable<{ machines: GisMachine[] }> {
    const url: string = `${this.machinesURL}/gis`;
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    if (filters != null) {
      options = MachinesService.setParameters(options, filters);
    }
    return this.http.get<{ machines: GisMachine[] }>(url, options);
  }

  public createMachine(machineData: MachinePersistRequest) {
    return this.http.post<any>(this.machinesURL, machineData, {observe: 'response'});
  }

  public editMachine(machineData: MachinePersistRequest) {
    const url: string = `${this.machinesURL}/${machineData.machineUuid}`;
    return this.http.put<any>(url, machineData, {observe: 'response'});
  }

  public deleteMachine(machineUuid: string) {
    const url: string = `${this.machinesURL}/${machineUuid}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public getMachineCommandSchemasByUuid(machineUuid: string): Observable<CommandSchemasResponse> {
    const url: string = `${this.machinesURL}/${machineUuid}/schemas`;
    return this.http.get<CommandSchemasResponse>(url);
  }

  public executeMachineCommand(machineUuid: string, schemaName: string, commandName: string,
                               commandPersistData: CommandPersistRequest): Observable<any> {
    const url: string = `${this.machinesURL}/${machineUuid}/schemas/${schemaName}/commands/${commandName}`;
    return this.http.post<any>(url, commandPersistData);
  }

  public getMachinesExcel(filters: MachineFilters, filename: string): Observable<Blob> {
    const url: string = `${this.machinesURL}/excel/${filename}`;
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    if (filters != null) {
      options = MachinesService.setParameters(options, filters);
    }
    return this.http.get(url, {params: options.params, responseType: 'blob'});
  }

  private static setParameters(options: any, filters: MachineFilters) {
    options.params["alias"] = FiltersUtils.getFilterTrim(filters.alias);
    options.params["serialNumber"] = FiltersUtils.getFilterTrim(filters.serialNumber);
    options.params["eui"] = FiltersUtils.getFilterTrim(filters.eui);
    options.params["isOn"] = FiltersUtils.getFilterBoolean(filters.isOn);
    options.params["isPoweredOn"] = FiltersUtils.getFilterBoolean(filters.isPoweredOn);
    options.params["organizationUuids"] = FiltersUtils.getFilter(filters.organizationUuids);
    options.params["machineModelUuids"] = FiltersUtils.getFilter(filters.machineModelUuids);
    options.params["countries"] = FiltersUtils.getFilter(filters.countries);
    options.params["states"] = FiltersUtils.getFilter(filters.states);
    options.params["localities"] = FiltersUtils.getFilter(filters.localities);
    options.params["communicationType"] = FiltersUtils.getFilter(filters.communicationType);
    options.params["from"] = FiltersUtils.getFilter(filters.from);
    options.params["until"] = FiltersUtils.getFilter(filters.until);
    options.params["deleted"] = FiltersUtils.getFilterBoolean(filters.deleted);
    options.params["machineGroupsUuids"] = FiltersUtils.getFilter(filters.machineGroupsUuids);
    options.params["alarmsDefinitionUuids"] = FiltersUtils.getFilter(filters.alarmsDefinitionUuids);
    options.params["simNumber"] = FiltersUtils.getFilterTrim(filters.simNumber);
    return options;
  }

  private static setPeriodParameters(from: number, until: number, viewInLiters: boolean) {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    options.params["from"] = FiltersUtils.getFilter(from);
    options.params["until"] = FiltersUtils.getFilter(until);
    options.params["viewInLiters"] = FiltersUtils.getFilterBoolean(viewInLiters);
    return options;
  }

}
